/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import youbora from 'youboralib';
import 'youbora-adapter-videojs';
import Component from 'can/component/component';
import DomEventsManager from 'scripts/dom-events-manager';
import appConfig from 'scripts/app-config';
import template from './live-video.stache';
import domUtils from 'scripts/utils/dom';
import deviceUtil from 'scripts/utils/device';
import dtm from 'scripts/services/dtm';
import Modernizr from 'modernizr';

const brightcoveOptions = _.get(window.globalSettings, 'brightcove');
const youboraOptions = _.get(window.globalSettings, 'youbora', {});

Component.extend({
    tag: 'card-live-video',
    template,
    leakScope: false,
    viewModel: {
        define: {
            isLiveStreamPossible: {
                type: 'boolean',
                value: false,
            },
            playerId: {
                type: 'string',
                value: brightcoveOptions.live.playerId,
            },
            accountId: {
                type: 'string',
                value: brightcoveOptions.accountId,
            },
            card: {
                Type: can.Map,
                Value: null,
            },
            activeCardIndex: {
                type: 'number',
                value: null,
            },
            cardIndex: {
                type: 'number',
                value: null,
            },
            videoHasStarted: {
                type: 'boolean',
                value: false,
            },
            isActiveCard: {
                type: 'boolean',
                get() {
                    return (
                        this.attr('cardIndex') == this.attr('activeCardIndex')
                    );
                },
            },
            muted: {
                type: 'boolean',
                value: false,
            },
            isMobile: {
                type: 'boolean',
                get() {
                    return deviceUtil.isMobile;
                },
            },
            isAdFinished: {
                type: 'boolean',
            },
            transactionCode: {
                type: 'string',
                value: null,
            },
            youboraAnalyticsIsLoaded: {
                type: 'boolean',
                value: false,
            },
        },
    },
    events: {
        inserted: function() {
            this.viewModel.element = this.element;
            DomEventsManager.subscribe(this);
            this.viewModel.attr(
                'transactionCode',
                `Live multiline video ${new Date().toISOString()}`,
            );

            if (
                this.viewModel.attr('accountId') &&
                this.viewModel.attr('playerId') &&
                this.viewModel.attr('card.streamId')
            ) {
                this.viewModel.attr('isLiveStreamPossible', true);
                _.delay(this.onWindowScroll.bind(this), 300);
            }
        },

        initLivePlayer() {
            const videoId = this.viewModel.attr('card.streamId');
            const playerUrl = _.get(appConfig, 'urls.api.brightcove.playerUrl');
            const accountId = _.get(brightcoveOptions, 'accountId');
            const playerId = _.get(brightcoveOptions, 'live.playerId');
            const isAdFinished = this.viewModel.attr('isAdFinished');
            let self = this;

            if (!playerUrl || !accountId || !playerId) {
                return console.error('Player is not configured correctly');
            }

            window._define = window.define;
            window.define = undefined;

            can.$.getScript(can.sub(playerUrl, { accountId, playerId }))
                .done((arg) => {
                    window.define = window._define;
                    let player = window
                        .videojs(this.element.find('video')[0])
                        .ready(function() {
                            const videoElement = this.el_.querySelector(
                                'video',
                            );

                            this.catalog.getVideo(videoId, (error, video) => {
                                if (error) {
                                    return console.error(
                                        `Video ${videoId} can not be loaded`,
                                    );
                                }
                                this.catalog.load(video);
                            });

                            this.one('play', () => {
                                if (!self.viewModel.attr('card.autoPlay')) {
                                    self.trackInlineVideoPlay(
                                        self.viewModel.attr('cardIndex'),
                                    );
                                }
                            });

                            this.on('play', () => {
                                this.controlBar.volumePanel.show();
                                self.viewModel.attr(
                                    'activeCardIndex',
                                    self.viewModel.attr('cardIndex'),
                                );
                                self.viewModel.attr('videoHasStarted', true);
                            });
                            // this.on('error', () => self.attr('videoIsPlaying', false));

                            Modernizr.on('videoautoplay', (hasAutoplay) => {
                                if (!hasAutoplay) {
                                    self.viewModel.attr('muted', true);
                                }

                                if (!isAdFinished) {
                                    this.one('ads-ad-started', () => {
                                        videoElement.muted = true;
                                    });
                                    this.one('ads-ad-ended', () => {
                                        self.viewModel.attr(
                                            'isAdFinished',
                                            true,
                                        );
                                        videoElement.muted = self.viewModel.attr(
                                            'muted',
                                        );
                                        this.play();
                                    });

                                    this.ima3.ready(() => {
                                        if (
                                            self.viewModel.attr(
                                                'card.autoPlay',
                                            ) &&
                                            domUtils.isOnScreen(
                                                self.element,
                                                true,
                                                false,
                                            )
                                        ) {
                                            this.muted(
                                                self.viewModel.attr('muted'),
                                            );
                                            this.play();
                                            self.trackDesktopAutoPlayAnalytics(
                                                self.viewModel.attr(
                                                    'cardIndex',
                                                ),
                                            );
                                        }
                                    });
                                } else {
                                    if (
                                        self.viewModel.attr('card.autoPlay') &&
                                        domUtils.isOnScreen(
                                            self.element,
                                            true,
                                            false,
                                        )
                                    ) {
                                        this.muted(
                                            self.viewModel.attr('muted'),
                                        );
                                        this.play();
                                        self.trackDesktopAutoPlayAnalytics(
                                            self.viewModel.attr('cardIndex'),
                                        );
                                    }
                                }
                            });

                            if (
                                !self.viewModel.attr('youboraAnalyticsIsLoaded')
                            ) {
                                self.setYouboraAnalytics(
                                    this,
                                    self.viewModel.attr('card'),
                                );
                                self.viewModel.attr(
                                    'youboraAnalyticsIsLoaded',
                                    true,
                                );
                            }
                        });

                    this.viewModel.attr(
                        'player',
                        window.videojs(this.element.find('video')[0]),
                    );
                })
                .fail(function(jqxhr, settings, exception) {
                    // this.viewModel.attr('videoHasStarted', false);
                });
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        onWindowScroll() {
            let player = this.viewModel.attr('player');
            if (domUtils.isOnScreen(this.element, true, false)) {
                if (!player) {
                    this.initLivePlayer();
                }
            } else {
                let player = this.viewModel.attr('player');
                if (player && !player.isFullscreen()) {
                    player.pause();
                }
            }
        },

        trackInlineVideoPlay: _.once((cardIndex) => {
            const data = 'Home Latest Feed Inline';
            dtm.trackTimelineLiveVideoActivity(data);
            dtm.trackLatestTimeline(cardIndex, 'Video Inline');
        }),

        trackDesktopAutoPlayAnalytics: _.debounce((cardIndex) => {
            if (!deviceUtil.isMobile) {
                const data = 'Home Latest Feed Inline Auto';
                dtm.trackTimelineLiveVideoActivity(data);
                dtm.trackLatestTimeline(cardIndex, 'Video Auto');
            }
        }, 1500),

        setYouboraAnalytics(player) {
            const plugin = new youbora.Plugin({
                accountCode: youboraOptions.accountCode,
            });
            plugin.setOptions({
                transactionCode: this.viewModel.attr('transactionCode'),
                'content.title': this.viewModel.attr('card.streamId'),
                'content.isLive': true,
                'extraparam.2': 'Live Video Card',
            });
            plugin.setAdapter(new youbora.adapters.Videojs(player));
            plugin.setAdsAdapter(
                new youbora.adapters.Videojs.GenericAdsAdapter(player),
            );
        },

        '{scope} activeCardIndex'(scope, event, activeCardIndex) {
            let player = this.viewModel.attr('player');

            if (!player) {
                return;
            }

            if (activeCardIndex != this.viewModel.attr('cardIndex')) {
                player.pause();
            } else {
                player.play();
            }
        },

        '.js-card-selector click'(element, event) {
            if (this.viewModel.attr('player')) {
                this.viewModel.attr('player').play();
            }
        },

        '.s-btn-box a click'() {
            const data = 'Home Latest Feed Inline CTA';
            dtm.trackTimelineLiveVideoActivity(data);
        },
    },
});
